/**
 * Pult düymələrinin normallaşdırılması.
 *
 * Tizen və webOS eyni ox/enter kodlarını verir, amma «Back» fərqlidir və
 * `KeyboardEvent.key` köhnə mühərriklərdə ya yoxdur, ya da `"Left"` kimi
 * köhnə adlarla gəlir. Ona görə BİRİNCİ mənbə `keyCode`-dur — o, TV
 * platformalarının böyük əksəriyyətində eynidir.
 *
 * ⚠ `event.key` EHTİYAT YOLDUR, ƏSAS YOL DEYİL (MYTasks 578): `keyCode`-u 0
 *   verən TV brauzerləri var (Vewd/Opera TV, bəzi VIDAA/WebView nüsxələri) və
 *   orada app tamamilə cavabsız qalırdı. Bax `KEY_NAMES`.
 */

export type RemoteKey =
  | 'up'
  | 'down'
  | 'left'
  | 'right'
  | 'enter'
  | 'back'
  | 'play'
  | 'pause'
  | 'playpause'
  | 'stop'
  | 'rewind'
  | 'forward'
  | 'next'
  | 'prev'
  | 'unknown'

const CODES: Record<number, RemoteKey> = {
  37: 'left',
  38: 'up',
  39: 'right',
  40: 'down',
  13: 'enter',

  // Back: 8 = klaviatura Backspace (dev), 27 = Esc (dev),
  // 461 = webOS Back, 10009 = Tizen Return.
  8: 'back',
  27: 'back',
  461: 'back',
  10009: 'back',

  // Media düymələri — player ekranı üçün.
  415: 'play',
  19: 'pause',
  10252: 'playpause',
  179: 'playpause', // klaviatura media düyməsi (dev)
  413: 'stop',
  32: 'playpause', // Space (dev)

  /**
   * ⚠ SARIMA DÜYMƏLƏRİ ƏSKİK İDİ (2026-08-04 auditi, sahib bildirişi «player
   *   funksional olmalıdır, yarımçıq etmisən»). `registerPlatformKeys` aşağıda
   *   `MediaRewind`/`MediaFastForward`-u Tizen-dən İSTƏYİRDİ, amma bu cədvəldə
   *   qarşılığı YOX İDİ → düymə pultda basılırdı, `toRemoteKey` 'unknown'
   *   qaytarırdı və heç nə olmurdu. Tizen və webOS eyni kodları verir.
   */
  412: 'rewind',
  417: 'forward',

  /**
   * ⚠ «NÖVBƏTİ TREK» — Android TV-dəki `KEYCODE_MEDIA_NEXT` qarşılığı
   *   (`TvPlayerScreen.kt`: düyməsi olan pultlarda serialda BİRBAŞA növbəti
   *   bölümə keçid, menyu açmadan). 10233 = Tizen `MediaTrackNext`,
   *   176 = standart brauzer media kodu (masaüstü klaviatura / webOS).
   * ⚠ 412/417-dəki ölçülmüş eyni tələ: kod bu cədvəldə olmasa
   *   `registerPlatformKeys` düyməni istəsə də `toRemoteKey` 'unknown'
   *   qaytarır və pultda basılan düymə heç nə etmir.
   */
  10233: 'next',
  176: 'next',

  /**
   * ⚠ «ƏVVƏLKİ TREK» — 547 auditində aşkarlanan ASİMMETRİYA. Android TV-də bu
   *   düymə Media3-ün `seekToPrevious()`-una düşür və tək bəndlik siyahıda
   *   sadəcə 0-a sarıyır (yəni orada da əvvəlki bölümə KEÇMİR — `TvPlayerScreen`
   *   yalnız `MEDIA_NEXT`-i tutur). Veb TV-də düymə ümumiyyətlə tanınmırdı.
   *   Burada o, «əvvəlki bölüm»ə bağlanır: pultda NEXT varsa PREV də var və
   *   istifadəçi ondan simmetrik davranış gözləyir.
   *   10232 = Tizen `MediaTrackPrevious`, 177 = standart brauzer media kodu.
   */
  10232: 'prev',
  177: 'prev',

  /**
   * ⚠ SƏHİFƏ/KANAL DÜYMƏLƏRİ (MYTasks 578, sahib sözü: «bütün TV brauzerlərində
   *   pultla idarə rahat olsun»). Bir çox TV pultunda ox düymələrinin yanında
   *   CH+/CH− (və ya P+/P−) var və istifadəçi siyahıda onlarla da hərəkət etməyə
   *   çalışır; masaüstü klaviaturasında eyni rolu PageUp/PageDown oynayır.
   *   Cədvəldə olmayan kod `toRemoteKey`-də 'unknown'-a düşür və `input.ts` onu
   *   SƏSSİZCƏ atır — yəni düymə basılır, heç nə olmur (412/417-də ölçülmüş
   *   eyni tələ).
   * ⚠ «SƏHİFƏ» ADDIMI DEYİL, ADİ YUXARI/AŞAĞI: fokus mühərriki sətir-sətir
   *   işləyir (`focus.ts` — `move`), «yarım ekran atla» anlayışı ümumiyyətlə
   *   yoxdur. Bu düymələri ora bağlamaq süni sıçrayış demək olardı.
   *   33/34 = PageUp/PageDown · 427/428 = webOS/Tizen ChannelUp/ChannelDown.
   */
  33: 'up',
  34: 'down',
  427: 'up',
  428: 'down',

  /**
   * ⚠ BRAUZERİN «GERİ» DÜYMƏSİ (166) — bəzi TV klaviaturalarında və Vewd/Opera
   *   TV pultlarında Back məhz bu kodla gəlir.
   * ⚠ 167 (BrowserForward) QƏSDƏN YOXDUR: app-da «irəli» naviqasiya anlayışı
   *   yoxdur (yığın yalnız geri açılır — `app.ts`), 'forward' isə BİZDƏ media
   *   sarımasıdır. Səhv bağlantı film oynadanda gözlənilməz sarıma verərdi.
   * ⚠ 10182 (Tizen «Exit») DA YOXDUR və bu, şüurlu qərardır: onu 'back'-ə
   *   bağlasaydıq `input.ts` həmin düymədə `preventDefault()` edərdi və Tizen-in
   *   ÖZ «app-dan çıx» davranışı bloklanardı — kökdə Back udulduğu üçün (§app.ts)
   *   düymə tamamilə ölü qalardı. Yəni bağlamamaq daha yaxşı davranışdır.
   */
  166: 'back',
}

/**
 * `KeyboardEvent.key` → pult düyməsi — YALNIZ `keyCode` boş olanda işlədilir.
 *
 * ⚠ NİYƏ LAZIMDIR (MYTasks 578): `keyCode` köhnəlmiş sahədir və bəzi TV
 *   brauzerləri (Vewd/Opera TV, bir sıra VIDAA və Android TV WebView nüsxələri)
 *   onu 0 verir. Belə cihazda app ÜMUMİYYƏTLƏ cavab vermir — nə ox, nə OK işləyir
 *   və heç bir xəta izi qalmır. Bu cədvəl həmin cihazlar üçün ehtiyat yoludur.
 * ⚠ HƏR İKİ MƏNBƏ EYNİ ANDA OXUNMUR: `keyCode` doludursa bura ümumiyyətlə
 *   çatılmır. Səbəb ölçülə bilər — bəzi mühərriklər eyni basış üçün hər ikisini
 *   verir və iki dəfə işlənsəydi bir basışda İKİ addım atılardı (girişin
 *   tək-xana nasosu bunu udur, amma şərti dar saxlamaq ucuzdur).
 * ⚠ KÖHNƏ ADLAR DA VAR (`Left`/`Up`/`Esc`): IE/köhnə WebKit adlandırması bəzi TV
 *   mühərriklərində qalıb (faylın başındakı qeyd).
 */
const KEY_NAMES: Record<string, RemoteKey> = {
  ArrowLeft: 'left',
  ArrowRight: 'right',
  ArrowUp: 'up',
  ArrowDown: 'down',
  Left: 'left',
  Right: 'right',
  Up: 'up',
  Down: 'down',
  Enter: 'enter',
  Select: 'enter',
  Backspace: 'back',
  Escape: 'back',
  Esc: 'back',
  GoBack: 'back',
  BrowserBack: 'back',
  XF86Back: 'back',
  PageUp: 'up',
  PageDown: 'down',
  ChannelUp: 'up',
  ChannelDown: 'down',
  MediaPlay: 'play',
  MediaPause: 'pause',
  MediaPlayPause: 'playpause',
  MediaStop: 'stop',
  MediaRewind: 'rewind',
  MediaFastForward: 'forward',
  MediaTrackNext: 'next',
  MediaTrackPrevious: 'prev',
}

/**
 * Brauzerin `keyCode`-unu məntiqi pult düyməsinə çevirir (Tizen · webOS ·
 * masaüstü ox düymələri · TAB).
 *
 * ⚠ TAB = «SAĞA», SHIFT+TAB = «SOLA» (sahib tələbi 2026-08-05: «veb tvdə mouse
 *   control tamamən çıxsın pult və tab effekti ilə olmalıdır»). Siçan qatı
 *   silinəndən sonra masaüstü brauzerdə app-ı gəzməyin yolu YALNIZ klaviaturadır;
 *   Tab isə brauzerin öz fokus ardıcıllığına düşür və bizim fokus mühərrikimizə
 *   HEÇ NƏ çatmır (elementlərimizdə `tabindex` yoxdur — o, TV-də lazımsız DOM
 *   işi deməkdir). Ona görə Tab bizim öz «növbəti element» addımımıza bağlanır:
 *   şəbəkə sətrin sonuna çatanda onsuz da aşağı sətrə keçir.
 * ⚠ `event.shiftKey` YALNIZ Tab üçün oxunur — digər düymələrdə modifikator
 *   davranışı dəyişmir.
 */
export function toRemoteKey(event: KeyboardEvent): RemoteKey {
  const code = event.keyCode || event.which
  if (code === 9) return event.shiftKey ? 'left' : 'right'
  if (code) return CODES[code] || 'unknown'

  // `keyCode` boşdur → `event.key` ehtiyatı (bax `KEY_NAMES` şərhi).
  const name = event.key
  if (!name) return 'unknown'
  if (name === 'Tab') return event.shiftKey ? 'left' : 'right'
  return KEY_NAMES[name] || 'unknown'
}

/** Ox düymələri ekranı sürüşdürməsin — brauzerin öz scroll-u layout tetikleyir. */
export function isNavigationKey(key: RemoteKey): boolean {
  return key === 'up' || key === 'down' || key === 'left' || key === 'right'
}

/**
 * Tizen-də «Back» düyməsi default olaraq brauzer tarixçəsinə düşür və app-dan
 * çıxır. Onu app-ın öz naviqasiyasına bağlamaq üçün qeydiyyatdan keçirmək
 * lazımdır. webOS-da belə tələb yoxdur.
 */
type TizenHost = {
  tizen?: {
    tvinputdevice?: { registerKey: (name: string) => void }
    application?: { getCurrentApplication: () => { exit: () => void } }
  }
}

/** Platformanın media düymələrini (oynat · dayandır · geri) qeydiyyata alır. */
export function registerPlatformKeys(): void {
  const host = window as unknown as TizenHost
  const device = host.tizen && host.tizen.tvinputdevice
  if (!device) return
  // ⚠ `MediaTrackNext` də istənilir (Android `KEYCODE_MEDIA_NEXT` pariteti) —
  //   qeydiyyatsız Tizen hadisəni app-a ÜMUMİYYƏTLƏ vermir (412/417 tələsi).
  const wanted = [
    'MediaPlayPause',
    'MediaPlay',
    'MediaPause',
    'MediaStop',
    'MediaRewind',
    'MediaFastForward',
    'MediaTrackNext',
    'MediaTrackPrevious',
    // ⚠ 2026-09-26: CODES-də 427/428 «up/down»a bağlıdır, amma qeydsiz düyməni Tizen app-a
    //   ümumiyyətlə vermir — kanal düymələri ilə sıra keçidi ölü idi.
    'ChannelUp',
    'ChannelDown',
  ]
  for (let i = 0; i < wanted.length; i++) {
    try {
      device.registerKey(wanted[i] as string)
    } catch {
      // Bu model həmin düyməni vermir — naviqasiya onsuz da işləyir.
    }
  }
}

/**
 * Ana ekranda «Back» app-dan çıxmalıdır (dead-end qadağandır, amma kökdə
 * çıxış GÖZLƏNİLƏN davranışdır — istifadəçi TV menyusuna qayıdır).
 */
export function exitApp(): void {
  const host = window as unknown as TizenHost
  const app = host.tizen && host.tizen.application
  if (app) {
    try {
      app.getCurrentApplication().exit()
      return
    } catch {
      // aşağıdakı webOS yoluna düşür
    }
  }
  // webOS: `window.close()` app-ı bağlayır. Brauzerdə (dev) heç nə etmir.
  window.close()
}
